import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

export interface ErrorInfo {
  message: string;
  code?: string;
  context: string;
  timestamp: Date;
  userFriendly: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  handleError(error: unknown, context?: string): void {
    try {
      // Filter out browser extension errors
      if (this.isBrowserExtensionError(error)) {
        return;
      }

      const errorInfo: ErrorInfo = {
        message: this.extractErrorMessage(error),
        code: this.extractErrorCode(error),
        context: context || 'Application',
        timestamp: new Date(),
        userFriendly: this.isUserFriendlyError(error)
      };

      this.logError(errorInfo);
      this.showUserNotification(errorInfo);
      this.handleNavigationError(errorInfo);
    } catch {
      // Error in error handler
    }
  }

  private extractErrorMessage(error: unknown): string {
    // Prefer server-provided message first (Angular's HttpErrorResponse.message is a generic string)
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      (error as { error?: unknown }).error &&
      typeof (error as { error?: unknown }).error === 'object' &&
      'message' in (error as { error?: { message?: string } }).error!
    ) {
      const nested = (error as { error: { message?: string } }).error?.message;
      if (nested) {
        return nested;
      }
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An unexpected error occurred';
  }

  private extractErrorCode(error: unknown): string | undefined {
    if (error && typeof error === 'object' && 'status' in error) {
      return `HTTP_${(error as { status: number }).status}`;
    }

    if (error && typeof error === 'object' && 'code' in error) {
      return (error as { code: string }).code;
    }

    return undefined;
  }

  private isUserFriendlyError(error: unknown): boolean {
    const userFriendlyCodes = ['HTTP_400', 'HTTP_404', 'HTTP_409', 'HTTP_422'];
    const code = this.extractErrorCode(error);
    return code ? userFriendlyCodes.includes(code) : false;
  }

  private isBrowserExtensionError(error: unknown): boolean {
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      const errorStack = error.stack?.toLowerCase() || '';

      // Check for content script errors
      if (
        errorMessage.includes('content.js') ||
        errorStack.includes('content.js') ||
        errorMessage.includes('chrome-extension') ||
        errorStack.includes('chrome-extension') ||
        errorMessage.includes('chrome-error://') ||
        errorStack.includes('chrome-error://')
      ) {
        return true;
      }
    }

    if (typeof error === 'string') {
      const errorStr = error.toLowerCase();
      return (
        errorStr.includes('content.js') ||
        errorStr.includes('chrome-extension') ||
        errorStr.includes('chrome-error://')
      );
    }

    return false;
  }

  private logError(errorInfo: ErrorInfo): void {
    // Intentionally minimal: hook for future logging integration
    void errorInfo;
  }

  private showUserNotification(errorInfo: ErrorInfo): void {
    if (errorInfo.userFriendly) {
      this.snackBar.open(errorInfo.message, 'Close', {
        duration: 7000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    } else {
      // Suppress generic message; prefer specific interceptor/component messages
      this.snackBar.open('', undefined as unknown as string, {
        duration: 0
      });
      this.snackBar.dismiss();
      return;
      /* this.snackBar.open('Something went wrong. Please try again.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      }); */
    }
  }

  private handleNavigationError(errorInfo: ErrorInfo): void {
    if (errorInfo.code === 'HTTP_401') {
      this.router.navigate(['/login']);
    } else if (errorInfo.code === 'HTTP_403') {
      this.router.navigate(['/unauthorized']);
    } else if (errorInfo.code === 'HTTP_500') {
      this.router.navigate(['/error']);
    }
  }

  clearErrors(): void {
    this.snackBar.dismiss();
  }
}
